import { useCallback, useEffect, useState, useRef } from "react";
import useEmblaCarousel from "embla-carousel-react";

export default function PeekCarousel({ slides, options }) {
  const [selectedIndex, setSelectedIndex] = useState(1);
  const [scrollSnaps, setScrollSnaps] = useState([]);
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const isMobile = screenWidth < 570;
  const axis = screenWidth < 570 ? "y" : "x";
  const isDesktop = axis === "x";
  const prevAxis = useRef(axis);

  const mobileOptions = {
    slidesToScroll: 1,
    align: "center",
    loop: true,
    containScroll: false,
    startIndex: Math.floor((slides?.length || 0) / 2),
    axis: "y",
    ...options,
  };

  const desktopOptions = {
    slidesToScroll: 1,
    align: "center",
    loop: false,
    containScroll: "keepSnaps", // ❗ This restricts dragging past visible slides
    startIndex: Math.floor((slides?.length || 0) / 2),
    axis: "x",
    ...options,
  };

  const [emblaRef, emblaApi] = useEmblaCarousel(
    isMobile ? mobileOptions : desktopOptions
  );

  const MIN_INDEX = 2;
  const MAX_INDEX = slides.length - 3;

  const isArrowPrevDisabled = isDesktop && selectedIndex <= MIN_INDEX;
  const isArrowNextDisabled = isDesktop && selectedIndex >= MAX_INDEX;

  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const onInit = useCallback((api) => {
    setScrollSnaps(api.scrollSnapList());
  }, []);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    const rawIndex = emblaApi.selectedScrollSnap();
    const total = slides.length;
    const fixedIndex = ((rawIndex % total) + total) % total;

    if (axis === "x" && (fixedIndex < MIN_INDEX || fixedIndex > MAX_INDEX)) {
      const clampedIndex = Math.max(MIN_INDEX, Math.min(MAX_INDEX, fixedIndex));
      emblaApi.scrollTo(clampedIndex);
    } else {
      setSelectedIndex(fixedIndex);
    }
  }, [emblaApi, slides.length, axis]);

  const scrollPrev = useCallback(() => {
    if (!emblaApi || isArrowPrevDisabled) return;
    emblaApi.scrollPrev();
  }, [emblaApi, isArrowPrevDisabled]);

  const scrollNext = useCallback(() => {
    if (!emblaApi || isArrowNextDisabled) return;
    emblaApi.scrollNext();
  }, [emblaApi, isArrowNextDisabled]);

  const scrollTo = useCallback(
    (index) => {
      if (!emblaApi) return;
      if (axis === "y" || (index >= MIN_INDEX && index <= MAX_INDEX)) {
        emblaApi.scrollTo(index);
      }
    },
    [emblaApi, axis]
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
        scrollPrev();
      } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
        scrollNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [scrollPrev, scrollNext, isArrowPrevDisabled, isArrowNextDisabled, axis]);

  //   useEffect(() => {
  //     const handleKeyDown = (event) => {
  //       if (event.key === "ArrowLeft" || event.key === "ArrowUp") {
  //         scrollPrev();
  //       } else if (event.key === "ArrowRight" || event.key === "ArrowDown") {
  //         scrollNext();
  //       }
  //     };
  //     window.addEventListener("keydown", handleKeyDown);
  //     return () => window.removeEventListener("keydown", handleKeyDown);
  //   }, [scrollPrev, scrollNext]);

  useEffect(() => {
    if (!emblaApi) return;
    onInit(emblaApi);
    onSelect();
    emblaApi.on("select", onSelect);
    emblaApi.on("reInit", () => {
      onInit(emblaApi);
      onSelect();
    });
    return () => emblaApi.off("select", onSelect);
  }, [emblaApi, onInit, onSelect]);

  const getDistanceFromSelected = (index) => {
    const total = slides.length;
    let distance = index - selectedIndex;
    if (distance > total / 2) distance -= total;
    if (distance < -total / 2) distance += total;
    return distance;
  };
  useEffect(() => {
    if (emblaApi) {
      // Wait for a short time to ensure everything is mounted
      const timeout = setTimeout(() => {
        scrollNext();
      }, 700); // Adjust delay as needed

      return () => clearTimeout(timeout);
    }
  }, [emblaApi]);
  useEffect(() => {
    if (!emblaApi) return;

    // On mount or when axis changes
    if (prevAxis.current !== axis) {
      const timeout = setTimeout(() => {
        scrollNext();
      }, 700); // Allow DOM/Embla to settle

      prevAxis.current = axis;

      return () => clearTimeout(timeout);
    }
  }, [emblaApi, axis, scrollNext]);

  const getScaleFromDistance = (distance) => {
    const abs = Math.abs(distance);
    return abs === 0
      ? 1
      : abs === 1
      ? 0.9
      : abs === 2
      ? 0.8
      : abs === 3
      ? 0.7
      : abs === 4
      ? 0.6
      : 0.5;
  };

  if (isMobile) {
    return (
      <div className="relative max-w-7xl mx-auto px-2 h-[80vh]">
        <div className="overflow-hidden h-full" ref={emblaRef}>
          <div className="flex flex-col h-full items-center">
            {slides.map((slide, index) => (
              <div
                key={index}
                className="embla__slide flex items-center justify-center mb-16"
                style={{
                  flex: "0 0 auto",
                  width: "80%",
                  maxWidth: "300px",
                }}
              >
                {slide}
              </div>
            ))}
          </div>
        </div>

        <div className="flex flex-col fixed right-4 top-1/2 -translate-y-1/2 gap-2">
          {scrollSnaps.map((_, index) => (
            <button
              key={index}
              className={`h-3 w-3 rounded-full transition-all ${
                index === selectedIndex
                  ? "bg-emerald-600 h-6"
                  : "bg-gray-300 hover:bg-gray-400"
              }`}
              onClick={() => scrollTo(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    );
  } else {
    return (
      <div className="relative max-w-7xl mx-auto px-2">
        <div className="overflow-hidden" ref={emblaRef}>
          <div className={`flex ${axis === "y" ? "flex-col" : ""}`}>
            {slides.map((slide, index) => {
              const distance = getDistanceFromSelected(index);
              const scale = getScaleFromDistance(distance);
              const opacity = Math.max(0.4, scale);
              const zIndex = 100 - Math.abs(distance);

              return (
                <div
                  key={index}
                  className={`${
                    axis === "y" ? "min-h-[20%]" : "min-w-[20%]"
                  } embla__slide transition-all duration-300 ease-in-out`}
                  style={{
                    transform: `scale(${scale})`,
                    opacity,
                    zIndex,
                    pointerEvents: "auto",
                  }}
                >
                  {slide}
                </div>
              );
            })}
          </div>
        </div>

        {isDesktop && (
          <>
            <button
              onClick={scrollPrev}
              className={`absolute -left-6 top-1/2 -translate-y-1/2 bg-white/80 w-10 h-10 rounded-full shadow-md z-10 flex items-center justify-center text-xl transition ${
                isArrowPrevDisabled ? "opacity-50 pointer-events-none" : ""
              }`}
              aria-label="Previous slide"
              disabled={isArrowPrevDisabled}
            >
              ←
            </button>
            <button
              onClick={scrollNext}
              className={`absolute -right-6 top-1/2 -translate-y-1/2 bg-white/80 w-10 h-10 rounded-full shadow-md z-10 flex items-center justify-center text-xl transition ${
                isArrowNextDisabled ? "opacity-50 pointer-events-none" : ""
              }`}
              aria-label="Next slide"
              disabled={isArrowNextDisabled}
            >
              →
            </button>
          </>
        )}

        <div className="flex justify-center mt-6 gap-2">
          {scrollSnaps.map((_, index) => {
            const isDotDisabled =
              isDesktop && (index < MIN_INDEX || index > MAX_INDEX);
            if (isDotDisabled) return null;

            return (
              <button
                key={index}
                className={`w-3 h-3 rounded-full transition-all ${
                  index === selectedIndex
                    ? "bg-emerald-600 w-6"
                    : "bg-gray-300 hover:bg-gray-400"
                }`}
                onClick={() => scrollTo(index)}
                aria-label={`Go to slide ${index}`}
              />
            );
          })}
        </div>
      </div>
    );
  }
}
